import { t } from '@lingui/core/macro';
import { isAxiosError, isCancel } from 'axios';

import type { ApiName } from './apis';
import { mapErrorResponse, type FieldError, type LegacyErrorPayload, type ResponseMessage } from './map-error-response';

export type ApiErrorKind = 'response' | 'network' | 'timeout' | 'canceled' | 'unknown';

export type FeedbackTone = 'warning' | 'error';

/** O toast que o legado mostraria (`handleOnError`) e os erros de campo de `INVALID_FORM`. */
export interface ErrorFeedback {
  tone: FeedbackTone;
  title: string;
  description: string;
  fieldErrors: FieldError[];
  code?: string;
  correlationId?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Paridade com `handleErrorUseQuery` do legado: com resposta e corpo, o corpo; sem corpo, só o
 * `code` do axios (`ERR_NETWORK`, `ECONNABORTED`…); fora do axios, `UNKNOWN`.
 */
export function toLegacyErrorPayload(error: unknown): LegacyErrorPayload {
  if (isAxiosError(error)) {
    const data: unknown = error.response?.data;
    // Corpo que não é objeto (HTML de gateway, texto): o legado caía na mensagem genérica.
    if (data) return isRecord(data) ? data : { type: 'error' };
    return { type: 'error', code: error.code, errors: [] };
  }
  return {
    type: 'error',
    message: t`Erro desconhecido. Por favor, entre em contato com o suporte técnico.`,
    code: 'UNKNOWN',
    errors: [],
  };
}

function kindOf(error: unknown): ApiErrorKind {
  if (isCancel(error)) return 'canceled';
  if (!isAxiosError(error)) return 'unknown';
  if (error.response) return 'response';
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return 'timeout';
  return 'network';
}

/** Erro normalizado de qualquer chamada de `shared/http`. O corpo cru do backend fica em `payload`. */
export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly kind: ApiErrorKind;
  readonly api: ApiName | undefined;
  readonly status: number | undefined;
  readonly code: string;
  readonly correlationId: string | undefined;
  readonly payload: LegacyErrorPayload;
  #mapped: ResponseMessage | undefined;

  constructor(init: { kind: ApiErrorKind; payload: LegacyErrorPayload; api?: ApiName; status?: number; cause?: unknown }) {
    super(typeof init.payload.message === 'string' ? init.payload.message : init.kind, { cause: init.cause });
    this.kind = init.kind;
    this.api = init.api;
    this.status = init.status;
    this.payload = init.payload;
    this.code = typeof init.payload.code === 'string' ? init.payload.code : 'UNKNOWN';
    this.correlationId = typeof init.payload.correlationId === 'string' ? init.payload.correlationId : undefined;
  }

  /** Converte qualquer coisa lançada numa chamada; `ApiError` passa direto. */
  static from(error: unknown, api?: ApiName): ApiError {
    if (error instanceof ApiError) return error;
    const status = isAxiosError(error) ? error.response?.status : undefined;
    return new ApiError({
      kind: kindOf(error),
      payload: toLegacyErrorPayload(error),
      ...(api ? { api } : {}),
      ...(status !== undefined ? { status } : {}),
      cause: error,
    });
  }

  get isCanceled() {
    return this.kind === 'canceled';
  }

  /** Resultado de `mapErrorResponse` do legado, calculado uma vez. */
  get mapped(): ResponseMessage {
    this.#mapped ??= mapErrorResponse(this.payload);
    return this.#mapped;
  }

  get fieldErrors(): FieldError[] {
    return this.mapped.fieldErrors ?? [];
  }

  /**
   * Paridade com `handleOnError`: aviso "Atenção!" quando `warningToastError` veio preenchido (quase
   * todo erro com `code`, inclusive `INVALID_FORM`); senão erro com o título padrão.
   */
  feedback(defaultTitle?: string): ErrorFeedback {
    const { errorMessage, fieldErrors, warningToastError, code, correlationId } = this.mapped;
    const base = {
      fieldErrors: fieldErrors ?? [],
      ...(code ? { code } : {}),
      ...(correlationId ? { correlationId } : {}),
    };
    if (warningToastError && warningToastError.length > 0) {
      return { tone: 'warning', title: t`Atenção!`, description: warningToastError, ...base };
    }
    return { tone: 'error', title: defaultTitle ?? t`Ops, ocorreu um erro!`, description: errorMessage, ...base };
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Erro de campo para o nome do campo, pela regra do legado: `name.toLowerCase().includes(key)`. */
export function fieldErrorFor(fieldErrors: readonly FieldError[], name: string): string | undefined {
  const lower = name.toLowerCase();
  return fieldErrors.find((error) => error.key && lower.includes(error.key))?.helpMessage;
}
